import { useQuery } from '@tanstack/react-query';
import { sessionApi } from '../api/session';
import type { SessionInfo } from '../api/session';
import { BUILD_VERSION, hasUpdate, isKnownVersion } from '../lib/appVersion';

/** How often an open tab asks the server what it is now serving. */
const POLL_MS = 60 * 1000;

export interface AppVersionState {
  /** raw session-info payload — also feeds the header's session countdown */
  sessionInfo?: SessionInfo;
  /** the release THIS tab is running (build-time stamp) */
  buildVersion: string;
  /** the release the SERVER is now serving (authenticated callers only) */
  serverVersion: string | null;
  /** server is serving something other than what this tab loaded → refresh needed */
  updateAvailable: boolean;
}

/**
 * One shared session-info poll — react-query dedupes it across every component
 * that calls this hook, so the header's version badge, the session countdown
 * and the update banner all ride on a single request per minute.
 *
 * `updateAvailable` is a plain inequality rather than a "server is newer"
 * comparison: a rollback needs the same refresh a roll-forward does.
 */
export function useAppVersion(): AppVersionState {
  const { data: sessionInfo } = useQuery({
    queryKey: ['session-info'],
    queryFn: sessionApi.info,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const serverVersion = isKnownVersion(sessionInfo?.version) ? sessionInfo!.version! : null;

  return {
    sessionInfo,
    buildVersion: BUILD_VERSION,
    serverVersion,
    updateAvailable: hasUpdate(BUILD_VERSION, serverVersion),
  };
}
